import moment from 'moment';

import { DATE_FORMAT_OUTPUT, MESSAGES_BLOCK_COUNT } from '../ConversationActions';

// Export LINE officiel : un .txt par conversation (« Export chat history » /
// « トーク履歴を送信 »). Pas de médias dans ce format : stickers / photos
// apparaissent comme placeholders localisés.
//
// Deux familles de fichiers :
// - mobile : horodatage + tabulation + expéditeur + tabulation + texte
// - desktop : horodatage + espace + expéditeur + espace + texte
//
// Locales supportées pour les en-têtes, méridiens et placeholders :
// les 7 langues du site (fr, en, es, de, it, nl, pt) + japonais (marché LINE).

const DATE_HEADER_REGEX = /^(\d{4})[./-](\d{1,2})[./-](\d{1,2})(?:\s*[（(][^）)]+[）)])?(?:\s+[A-Za-zàâäéèêëïîôùûüçÀÂÄÉÈÊËÏÎÔÙÛÜÇ.]+)?\s*$/;

// Heure 24h ou 12h, avec préfixes JA/ZH et suffixes AM/PM des locales du site.
const TIME_PREFIX = '(?:午前|午後|上午|下午|오전|오후)?\\s*';
const TIME_SUFFIX = '(?:\\s*(?:AM|PM|a\\.m\\.|p\\.m\\.|A\\.M\\.|P\\.M\\.))?';
const TIME_CORE = '([0-2]?\\d)[:.．]([0-5]\\d)(?::([0-5]\\d))?';
const TIME_TOKEN = `${TIME_PREFIX}${TIME_CORE}${TIME_SUFFIX}`;

const MOBILE_MESSAGE_REGEX = new RegExp(
    `^${TIME_TOKEN}\\t([^\\t]+)\\t([\\s\\S]*)$`,
    'i',
);

// Desktop : pas de tabulation. Le premier token après l'heure est l'expéditeur
// (les noms multi-mots sont fiables sur mobile grâce aux tabulations).
const DESKTOP_MESSAGE_REGEX = new RegExp(
    `^${TIME_TOKEN}\\s+(\\S+)\\s+([\\s\\S]+)$`,
    'i',
);

const TITLE_PATTERNS = [
    /\[LINE\]\s*(?:Chat history with|Chat with)\s+(.+)/i,
    /^Chat history with\s+(.+)/i,
    /^Chat with\s+(.+)/i,
    /Historique (?:de (?:la )?discussion|du chat) avec\s+(.+)/i,
    /Discussion avec\s+(.+)/i,
    /Historial (?:del chat|de chat) con\s+(.+)/i,
    /Chat con\s+(.+)/i,
    /Chatverlauf mit\s+(.+)/i,
    /Chat mit\s+(.+)/i,
    /Cronologia chat con\s+(.+)/i,
    /Chat con\s+(.+)/i,
    /Chatgeschiedenis met\s+(.+)/i,
    /Chat met\s+(.+)/i,
    /Hist[oó]rico (?:do chat|de conversa) com\s+(.+)/i,
    /Conversa com\s+(.+)/i,
    /(.+)\s+とのトーク履歴/,
    /(.+)とのトーク/,
];

const META_LINE_REGEXES = [
    /^\[LINE\]/i,
    /^Saved on\s*:/i,
    /^Enregistr[eé]\s+le\s*:/i,
    /^Guardado el\s*:/i,
    /^Gespeichert am\s*:/i,
    /^Salvato il\s*:/i,
    /^Opgeslagen op\s*:/i,
    /^Guardado em\s*:/i,
    /^保存日時\s*[:：]/,
    /^Save Date\s*:/i,
];

// Messages annulés : exclus (pas de contenu imprimable).
const UNSENT_REGEXES = [
    /^you unsent a message\.?$/i,
    /^unsent a message\.?$/i,
    /unsent a message\.?$/i,
    /^vous avez annul[eé] (?:un|le) message\.?$/i,
    /a annul[eé] (?:un|le) message\.?$/i,
    /^has cancelado un mensaje\.?$/i,
    /cancel[oó] un mensaje\.?$/i,
    /^du hast eine nachricht gel[oö]scht\.?$/i,
    /hat eine nachricht (?:zur[uü]ckgezogen|gel[oö]scht)\.?$/i,
    /^hai annullato un messaggio\.?$/i,
    /ha annullato un messaggio\.?$/i,
    /^je hebt een bericht verwijderd\.?$/i,
    /heeft een bericht (?:verwijderd|teruggetrokken)\.?$/i,
    /^cancelaste uma mensagem\.?$/i,
    /cancelou uma mensagem\.?$/i,
    /^メッセージの送信を取り消しました$/,
    /がメッセージの送信を取り消しました$/,
    /已收回訊息$/,
    /^您已收回訊息$/,
    /^you deleted a message\.?$/i,
];

// Placeholders média LINE (toutes langues site + JA/ZH/KO).
// Conservés tels quels dans le livre pour signaler qu'un média était présent.
const MEDIA_PLACEHOLDER_REGEX = /^\[(?:Photo|Image|Sticker|Stamp|Video|File|Files|GIF|Voice message|Audio|Voice|Location|Contact|Note|Album|Photos?|Images?|Vid[eé]o|Fichier|Fichiers|Autocollant|Message vocal|Note vocale|Emplacement|Position|Lieu|Contacto|Ubicaci[oó]n|Nota de voz|Mensaje de voz|Archivo|Archivos|Foto|Sticker|Video|Datei|Dateien|Sprachnachricht|Standort|Kontakt|Notiz|File|Messaggio vocale|Posizione|Contatto|Nota|Bestand|Bestanden|Spraakbericht|Locatie|Contact|Notitie|Ficheiro|Ficheiros|Mensagem de voz|Localiza[cç][aã]o|Nota|写真|畫[像面]|貼圖|スタンプ|動画|影片|ファイル|ボイスメッセージ|音声メッセージ|位置情報|連絡先|ノート|사진|동영상|스티커|파일|음성메시지|위치)\]$/i;

const DATE_FORMATS = [
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD HH:mm',
    'YYYY/MM/DD HH:mm:ss',
    'YYYY/MM/DD HH:mm',
    'YYYY.MM.DD HH:mm:ss',
    'YYYY.MM.DD HH:mm',
];

export function readFile(file, options, onProgress) {
    if(isTextFile(file)) {
        return new Promise(resolve => {
            const reader = new FileReader();
            reader.onload = () => {
                onProgress(100);
                resolve([
                    {
                        path: file.name,
                        filename: file.name,
                        originalContent: reader.result,
                        content: transformEntry(reader.result),
                    },
                ]);
            };
            reader.readAsText(file, 'UTF-8');
        });
    }
    return Promise.reject(new Error('LineImportRequiresTxt'));
}

export function getFiles() {
    return Promise.resolve([]);
}

export function canHandleImages() {
    return false;
}

export function getProfileName(entries) {
    const conversation = getConversation(entries[0]);
    if(conversation.profileName) {
        return conversation.profileName;
    }
    return (conversation.participants || [])[0] || '';
}

export function getThreads() {
    // Un export LINE = une seule conversation (comme WhatsApp).
    return [];
}

export function getArchiveContent(entries, from, thread, options = { withImages: false, withMedias: false }, onProgress = percentile => {}) {
    const participants = [];
    const messagesBlocks = [];

    onProgress(1);

    let messages = [];
    (entries || []).forEach(entry => {
        const conversation = getConversation(entry);
        (conversation.participants || []).forEach(name => {
            if(name && !participants.includes(name)) {
                participants.push(name);
            }
        });
        messages = messages.concat(conversation.messages || []);
    });

    const profileName = from || getProfileName(entries);

    messages = messages
        .map(message => formatMessage(message, profileName, participants))
        .filter(message => message && message.messageCsv)
        .sort((messageA, messageB) => messageA.timestamp - messageB.timestamp);

    // Le titulaire de l'export doit être en tête de liste (bulle « moi »).
    if(profileName && participants.includes(profileName)) {
        participants.splice(participants.indexOf(profileName), 1);
        participants.unshift(profileName);
    }

    const iteration = Math.floor(messages.length / MESSAGES_BLOCK_COUNT);
    const percentile = 100 / (iteration + 1);

    for(let i = 0; i <= iteration; i += 1) {
        const messagesBlock = messages.slice(i * MESSAGES_BLOCK_COUNT, (i * MESSAGES_BLOCK_COUNT) + MESSAGES_BLOCK_COUNT);
        onProgress(percentile);

        if(messagesBlock.length) {
            let csv = '';
            messagesBlock.forEach(message => {
                csv += `${message.messageCsv}\n`;
            });

            messagesBlocks.push({
                sort: i,
                filename: `messages_${i + 1}.csv`,
                content: csv,
                messages: messagesBlock,
                start: new Date(messagesBlock[0].timestamp),
                end: new Date(messagesBlock[messagesBlock.length - 1].timestamp),
            });
        }
    }

    return {
        participants,
        messagesBlocks,
        images: [],
        medias: [],
    };
}

function isTextFile(file) {
    const type = `${(file || {}).type || ''}`.toLowerCase();
    const name = `${(file || {}).name || ''}`.toLowerCase();
    return type.includes('text') || type.includes('plain') || name.endsWith('.txt');
}

function getConversation(entry) {
    if(!entry) {
        return { participants: [], messages: [] };
    }
    if(entry.originalContent && typeof entry.originalContent === 'string') {
        return JSON.parse(transformEntry(entry.originalContent));
    }
    if(typeof entry.content === 'string') {
        try {
            return JSON.parse(entry.content);
        } catch (err) {
            return JSON.parse(transformEntry(entry.content));
        }
    }
    return entry.content || { participants: [], messages: [] };
}

function transformEntry(rawContent) {
    const lines = `${rawContent || ''}`
        .replace(/^\uFEFF/, '')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .split('\n');

    const participants = [];
    const messages = [];
    let currentDate = null;
    let contactName = '';
    let pending = null;

    lines.forEach(rawLine => {
        const line = normalizeLine(rawLine);

        if(!line) {
            return;
        }

        if(isMetaLine(line)) {
            contactName = contactName || extractContactFromTitle(line);
            return;
        }

        const dateHeader = parseDateHeader(line);
        if(dateHeader) {
            flushPending(pending, messages, participants);
            pending = null;
            currentDate = dateHeader;
            return;
        }

        if(!currentDate) {
            contactName = contactName || extractContactFromTitle(line);
            return;
        }

        const parsed = parseMessageLine(line, currentDate);
        if(parsed) {
            flushPending(pending, messages, participants);
            pending = parsed;
            return;
        }

        // Continuation multiligne du message en cours.
        if(pending) {
            pending.text = pending.text ? `${pending.text}\n${line}` : line;
        }
    });

    flushPending(pending, messages, participants);

    const profileName = resolveProfileName(participants, contactName);

    return JSON.stringify({
        participants,
        messages,
        profileName,
        contactName,
    });
}

function flushPending(pending, messages, participants) {
    if(!pending) {
        return;
    }
    if(isUnsentMessage(pending.sender, pending.text)) {
        return;
    }
    if(!pending.text || !pending.text.trim()) {
        return;
    }
    if(!participants.includes(pending.sender)) {
        participants.push(pending.sender);
    }
    messages.push(pending);
}

function normalizeLine(line) {
    return `${line || ''}`
        .replace(/[\u200E\u200F\uFEFF\u200B]/g, '')
        .replace(/[\u202F\u00A0]/g, ' ')
        .replace(/\u3000/g, ' ')
        .replace(/．/g, '.')
        .replace(/\s+$/g, '');
}

function isMetaLine(line) {
    return META_LINE_REGEXES.some(regex => regex.test(line));
}

function extractContactFromTitle(line) {
    for(let i = 0; i < TITLE_PATTERNS.length; i += 1) {
        const match = line.match(TITLE_PATTERNS[i]);
        if(match && match[1]) {
            return match[1].replace(/\s+on\s+\d{4}.*/i, '').trim();
        }
    }
    return '';
}

function parseDateHeader(line) {
    const match = line.match(DATE_HEADER_REGEX);
    if(!match) {
        return null;
    }
    const year = match[1];
    const month = match[2].padStart(2, '0');
    const day = match[3].padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function parseMessageLine(line, currentDate) {
    const mobileMatch = line.match(MOBILE_MESSAGE_REGEX);
    if(mobileMatch) {
        return buildPending(
            line,
            currentDate,
            mobileMatch[1],
            mobileMatch[2],
            mobileMatch[3] || '00',
            (mobileMatch[4] || '').trim(),
            (mobileMatch[5] || '').trim(),
        );
    }

    const desktopMatch = line.match(DESKTOP_MESSAGE_REGEX);
    if(desktopMatch) {
        const sender = (desktopMatch[4] || '').trim();
        const text = (desktopMatch[5] || '').trim();
        if(!sender || !text) {
            return null;
        }
        return buildPending(
            line,
            currentDate,
            desktopMatch[1],
            desktopMatch[2],
            desktopMatch[3] || '00',
            sender,
            text,
        );
    }

    return null;
}

function buildPending(rawLine, currentDate, hour, minute, second, sender, text) {
    if(!sender) {
        return null;
    }

    const hour24 = toHour24(rawLine, hour);
    const dateStr = `${currentDate} ${String(hour24).padStart(2, '0')}:${minute}:${second}`;
    const date = moment(dateStr, DATE_FORMATS, true);
    if(!date.isValid()) {
        return null;
    }

    return {
        date: date.format(DATE_FORMAT_OUTPUT).split('+').shift(),
        timestamp: date.valueOf(),
        sender,
        text: normalizeMessageText(text),
    };
}

function toHour24(rawLine, hour) {
    let value = parseInt(hour, 10);
    const lower = `${rawLine}`.toLowerCase();

    const isPm = /(?:^|[\s\t])(?:pm|p\.m\.|午後|下午|오후)(?:\s|$|\t)/i.test(lower)
        || /午後|下午|오후/.test(rawLine);
    const isAm = /(?:^|[\s\t])(?:am|a\.m\.|午前|上午|오전)(?:\s|$|\t)/i.test(lower)
        || /午前|上午|오전/.test(rawLine);

    if(isPm && value < 12) {
        value += 12;
    }
    if(isAm && value === 12) {
        value = 0;
    }
    return value;
}

function normalizeMessageText(text) {
    const trimmed = `${text || ''}`.trim();
    if(MEDIA_PLACEHOLDER_REGEX.test(trimmed)) {
        return trimmed;
    }
    return trimmed;
}

function isUnsentMessage(sender, text) {
    const combined = `${text || ''}`.trim();
    const withSender = `${sender || ''}${combined}`.trim();
    return UNSENT_REGEXES.some(regex => regex.test(combined) || regex.test(withSender));
}

function resolveProfileName(participants, contactName) {
    if(contactName) {
        const others = participants.filter(name => name !== contactName);
        if(others.length === 1) {
            return others[0];
        }
        if(others.length > 1) {
            return others[0];
        }
    }
    return participants[0] || '';
}

function formatMessage(message, profileName, participants) {
    const sender = `${message.sender || ''}`.replace(/;/g, ',');
    const isSender = sender === profileName;
    const recipients = participants.filter(name => name !== sender);
    const to = (isSender ? (recipients[0] || profileName) : profileName).replace(/;/g, ',');

    const data = {
        date: message.date,
        sender,
        to,
        content: encodeURIComponent(message.text || ''),
        isSender: isSender ? 'true' : 'false',
        medias: '',
    };

    return {
        messageParticipant: sender,
        messageCsv: Object.keys(data).map(key => (data[key] || '')).join(';'),
        date: message.date,
        timestamp: message.timestamp,
        text: message.text,
        sender,
    };
}
